import React, { useEffect, useState } from 'react';
import { api } from './api';
import { t } from './i18n';

// Palette mirrors App.js COLORS so this page feels native.
const C = {
  primary: '#1a4d8f',
  primaryDark: '#0d3163',
  accent: '#d4af37',
  bg: '#f7f8fa',
  card: '#ffffff',
  text: '#1f2937',
  muted: '#6b7280',
  border: '#e5e7eb',
};

// Vanity domains the org owns, keyed by the exact country/city name the
// directory uses (matches the ?country= / ?city= filter values). These are
// marketing redirects that all point back into Jewish Compass, so here we
// surface them as shareable labels while linking internally for fast SPA nav.
const COUNTRY_DOMAINS = {
  Austria: 'jewishaustria.com',
  Belgium: 'jewishbelgium.com',
  Croatia: 'jewishcroatia.com',
  'Czech Republic': 'jewishczech.com',
  Denmark: 'jewishdenmark.com',
  France: 'jewishfrance.org',
  Germany: 'jewishgermany.org',
  Greece: 'jewishgreece.org',
  Hungary: 'jewishungary.com',
  Luxembourg: 'jewishluxembourg.com',
  Mexico: 'jewishmexico.com',
  Netherlands: 'jewishnetherlands.com',
  Poland: 'jewishpoland.net',
  Portugal: 'jewishportugal.com',
  Spain: 'jewishspain.org',
  Switzerland: 'jewishswitzerland.org',
  'United Kingdom': 'jewishuk.com',
  Ukraine: 'jewishukraine.com',
};

// City → { country, domain }. Grouped under their country card below.
const CITY_DOMAINS = [
  { city: 'Vienna', country: 'Austria', domain: 'jewishvienna.org' },
  { city: 'Brussels', country: 'Belgium', domain: 'jewishbrussels.com' },
  { city: 'Prague', country: 'Czech Republic', domain: 'jewishprague.org' },
  { city: 'Paris', country: 'France', domain: 'jewishparis.org' },
  { city: 'Budapest', country: 'Hungary', domain: 'jewishbudapest.org' },
  { city: 'Rome', country: 'Italy', domain: 'jewishrome.com' },
  { city: 'Milan', country: 'Italy', domain: 'jewishmilan.com' },
  { city: 'Florence', country: 'Italy', domain: 'jewishflorence.org' },
  { city: 'Venice', country: 'Italy', domain: 'jewishvenice.info' },
  { city: 'Barcelona', country: 'Spain', domain: 'jewishbarcelona.org' },
  { city: 'Madrid', country: 'Spain', domain: 'jewishmadrid.com' },
  { city: 'Geneva', country: 'Switzerland', domain: 'jewishgeneva.com' },
  { city: 'Zurich', country: 'Switzerland', domain: 'jewishzurich.com' },
  { city: 'London', country: 'United Kingdom', domain: 'jewishlondon.info' },
];

const citiesFor = (country) => CITY_DOMAINS.filter((c) => c.country === country);

// Resolve the vanity domain for a set of list filters. A city match wins over
// its country. Case-insensitive so lowercase URL params still match.
export function vanityDomainFor({ country, city } = {}) {
  if (city) {
    const hit = CITY_DOMAINS.find((c) => c.city.toLowerCase() === String(city).toLowerCase());
    if (hit) return { domain: hit.domain, scope: hit.city };
  }
  if (country) {
    const key = Object.keys(COUNTRY_DOMAINS).find((k) => k.toLowerCase() === String(country).toLowerCase());
    if (key) return { domain: COUNTRY_DOMAINS[key], scope: key };
  }
  return null;
}

// A "share this page" strip for the list view — shows the destination's
// memorable domain with a copy button.
export function ShareThisPage({ domain, scope }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = `https://${domain}`;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1600); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else { done(); }
  };
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      background: 'rgba(26,77,143,0.06)', border: `1px solid ${C.border}`,
      borderRadius: 999, padding: '6px 12px', fontSize: 13, color: C.text,
    }}>
      <span aria-hidden="true">🔗</span>
      <span style={{ color: C.muted }}>{t('Share')} {scope ? `${scope}` : t('this page')}:</span>
      <span style={{ fontWeight: 700, color: C.primary }}>{domain}</span>
      <button
        type="button"
        onClick={copy}
        style={{
          background: copied ? '#059669' : C.primary, color: 'white', border: 'none',
          borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {copied ? t('✓ Copied') : t('Copy')}
      </button>
    </div>
  );
}

// Small copy-to-clipboard icon button, sat beside a domain link so the domain
// itself stays a navigable link while the icon handles "grab the URL to share".
function CopyBtn({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1400); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(done);
    } else { done(); }
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? t('Copied') : `${t('Copy')} ${url}`}
      aria-label={copied ? t('Copied') : `${t('Copy')} ${url}`}
      style={{
        display: 'inline-flex', alignItems: 'center', background: 'transparent',
        border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 13,
        color: copied ? '#059669' : C.muted, fontFamily: 'inherit', lineHeight: 1,
      }}
    >
      <span aria-hidden="true">{copied ? '✓' : '⧉'}</span>
    </button>
  );
}

// A vanity domain rendered as a link to its destination page, with a copy
// button beside it. Same treatment for both country- and city-level domains.
function DomainLink({ href, domain }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%',
      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 999, padding: '3px 8px',
    }}>
      <a
        href={href}
        style={{ color: C.primary, textDecoration: 'none', fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {domain}
      </a>
      <CopyBtn url={`https://${domain}`} />
    </span>
  );
}

function CountryCard({ country, count, domain, cities }) {
  const comingSoon = !count;
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <a
          href={`/list?country=${encodeURIComponent(country)}`}
          style={{ fontSize: 16, fontWeight: 700, color: C.primary, textDecoration: 'none' }}
        >
          {country}
        </a>
        {comingSoon ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>{t('coming soon')}</span>
        ) : (
          <span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{Number(count).toLocaleString()} place{count === 1 ? '' : 's'}</span>
        )}
      </div>

      {domain && (
        <div><DomainLink href={`/list?country=${encodeURIComponent(country)}`} domain={domain} /></div>
      )}

      {cities.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 2 }}>
          {cities.map((c) => (
            <div key={c.city} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <a
                href={`/list?country=${encodeURIComponent(country)}&city=${encodeURIComponent(c.city)}`}
                style={{ fontSize: 12.5, fontWeight: 600, color: C.text, textDecoration: 'none', minWidth: 64 }}
              >
                {c.city}
              </a>
              <DomainLink href={`/list?country=${encodeURIComponent(country)}&city=${encodeURIComponent(c.city)}`} domain={c.domain} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DestinationsPage() {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    api.countries()
      .then((rows) => {
        const m = {};
        (rows || []).forEach((r) => { if (r && r.country) m[r.country] = parseInt(r.count, 10) || 0; });
        setCounts(m);
      })
      .catch(() => setCounts({}));
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Build the display set: every covered country (live counts) plus any
  // country/city we own a domain for, so nothing with a domain is hidden even
  // before it has listings.
  const names = new Set();
  if (counts) Object.keys(counts).forEach((n) => names.add(n));
  Object.keys(COUNTRY_DOMAINS).forEach((n) => names.add(n));
  CITY_DOMAINS.forEach((c) => names.add(c.country));

  const cards = [...names]
    .map((country) => ({
      country,
      count: counts ? (counts[country] || 0) : 0,
      domain: COUNTRY_DOMAINS[country] || null,
      cities: citiesFor(country),
    }))
    .sort((a, b) => a.country.localeCompare(b.country));

  return (
    <div style={{ background: C.bg, minHeight: '60vh' }}>
      <div style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`, color: 'white', padding: '28px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 6px' }}>{t('Explore Jewish life by destination')}</h1>
          <p style={{ fontSize: 15, opacity: 0.9, margin: 0 }}>
            {t('Every country and city we cover — each with an easy-to-share link. Tap a domain to copy it.')}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px' }}>
        {!counts ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>{t('Loading destinations…')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
            {cards.map((c) => (
              <CountryCard key={c.country} country={c.country} count={c.count} domain={c.domain} cities={c.cities} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
